// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Anchor, Grid, Group, Paper, Stack, Text, ThemeIcon, Title } from '@mantine/core';
import type { Composition, DiagnosticReport } from '@medplum/fhirtypes';
import { IconClipboardText, IconFileDescription } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useNavigate } from 'react-router';
import classes from './SupplementalInformationSection.module.css';

export interface SupplementalInformationSectionProps {
  intakeComposition: Composition | undefined;
  hsatDiagnosticReport: DiagnosticReport | undefined;
  patientId: string;
  loading: boolean;
  intakeCompleted?: boolean;
  onOpenScreener?: () => void;
}

function renderLink(
  loading: boolean,
  available: boolean,
  linkLabel: string,
  emptyLabel: string,
  onClick: () => void
): JSX.Element {
  if (loading) {
    return (
      <Text size="sm" c="dimmed">
        Loading…
      </Text>
    );
  }
  if (available) {
    return (
      <Anchor component="button" type="button" onClick={onClick} size="sm" fw={500}>
        {linkLabel}
      </Anchor>
    );
  }
  return (
    <Text size="sm" c="dimmed">
      {emptyLabel}
    </Text>
  );
}

export function SupplementalInformationSection({
  intakeComposition,
  hsatDiagnosticReport,
  patientId,
  loading,
  intakeCompleted = Boolean(intakeComposition?.id),
  onOpenScreener,
}: SupplementalInformationSectionProps): JSX.Element {
  const navigate = useNavigate();
  const screenerAction = onOpenScreener ?? (() => navigate(`/Patient/${patientId}/intake-screener`));

  return (
    <Paper withBorder radius="md" shadow="sm" p="xl">
      <Title order={2} mb="md">
        Supplemental Information
      </Title>
      <Grid>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Paper withBorder radius="md" p="md" className={classes.card}>
            <Group align="flex-start" wrap="nowrap">
              <ThemeIcon size="xl" radius="md" variant="light" color="polaris">
                <IconClipboardText size={24} />
              </ThemeIcon>
              <Stack gap={4} style={{ flex: 1 }}>
                <Text fw={600}>Intake Screener</Text>
                {renderLink(
                  loading,
                  Boolean(intakeComposition?.id) || Boolean(onOpenScreener),
                  intakeCompleted ? 'View Screener' : 'Complete',
                  'Not captured yet',
                  screenerAction
                )}
              </Stack>
            </Group>
          </Paper>
        </Grid.Col>
        {(loading || hsatDiagnosticReport?.id) && (
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Paper withBorder radius="md" p="md" className={classes.card}>
              <Group align="flex-start" wrap="nowrap">
                <ThemeIcon size="xl" radius="md" variant="light" color="polaris">
                  <IconFileDescription size={24} />
                </ThemeIcon>
                <Stack gap={4} style={{ flex: 1 }}>
                  <Text fw={600}>HSAT Results</Text>
                  {renderLink(loading, Boolean(hsatDiagnosticReport?.id), 'View Report', 'No results on file', () =>
                    navigate(`/Patient/${patientId}/diagnostic-reports`)
                  )}
                </Stack>
              </Group>
            </Paper>
          </Grid.Col>
        )}
      </Grid>
    </Paper>
  );
}
